// Lógica pura dos cards do Dwight (HydraDrawer) — sem antd, testável no jest.
//
// Por que existe (2026-09-06): o card "Atualizar proposta" mostrava 120
// caracteres de JSON cortado no meio da palavra e a pessoa aprovava às cegas.
// Foi assim que 10.337 caracteres de descrição técnica viraram 2.412 na
// PRP-2026-0175, cinco vezes seguidas. O card agora diz O QUE muda e QUANTO,
// e grita quando o texto encolhe ou a proposta já foi enviada ao cliente.

export const ROTULO_CAMPO_PROPOSTA: Record<string, string> = {
  descricao_tecnica: 'descrição técnica',
  entregaveis: 'entregáveis',
  cronograma: 'cronograma',
  prazo_entrega: 'prazo de entrega',
  itens_nao_integram: 'itens que não integram',
  responsabilidades_contratada: 'responsabilidades da contratada',
  responsabilidades_contratante: 'responsabilidades do contratante',
  medicoes: 'medições',
  condicoes_pagamento: 'condições de pagamento',
};

export const CAMPOS_TEXTO_PROPOSTA = Object.keys(ROTULO_CAMPO_PROPOSTA);

// `recuperada` é o registro de auditoria da tentativa parcial. O vínculo é
// produzido e validado no backend; mensagens antigas sem esse vínculo mantêm
// o alerta porque não há como provar qual campo o bloco cortado pretendia usar.
export function filtrarAcoesVisiveis<T extends {
  id?: string; status?: string; tool?: string; input?: any; recuperou_truncamento?: boolean;
}>(acoes: T[] | null | undefined): Array<T & { recuperou_truncamento?: boolean }> {
  return Array.isArray(acoes) ? acoes.filter(a => a.status !== 'recuperada') : [];
}

// O que muda num campo, parágrafo por parágrafo (22/09/2026, PRP-2026-0178).
// Calculado no agentes-engine (handlers/documentoProposta.js → compararBlocos):
// a tela só mostra, não recalcula.
export interface MudancasCampo {
  total: number;
  total_novo?: number;
  intactos: number | null;
  trocas?: { antes: string; depois: string }[];
  somem?: string[];
  novos?: string[];
  n_trocas?: number | null;
  n_somem?: number | null;
  n_novos?: number | null;
  resumido?: boolean;
}
export interface PreviaCampo { campo: string; chars_antes: number; chars_depois: number; mudancas?: MudancasCampo }
export interface PreviaAtualizacao {
  numero_proposta?: string;
  proposta_gerada_id?: string;
  status_doc?: string | null;
  base_atualizado_em?: string | null;
  nao_encontrada?: boolean;
  campos?: PreviaCampo[];
  // Proposta certa (15/09/2026): QUAL documento — revisão, título e negócio.
  revisao?: number | null;
  titulo_documento?: string | null;
  negocio_id?: number | null;
  negocio_titulo?: string | null;
  tem_link?: boolean;
}

function cortarTexto(s: unknown, max: number): string {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

// "PRP-2026-0102 R0 · título do documento · negócio nº 1042 — título". No
// incidente da PRP-2026-0110 (15/09/2026) o card dizia só "Proposta PRP-2026-0110"
// e ninguém viu que era a proposta de OUTRO negócio. Mesma régua do agentes-engine
// (handlers/alvoProposta.js → descreverAlvoProposta). Card antigo, sem o
// documento na prévia, fica só com o número.
export function descreverAlvoProposta(numero: string | null | undefined, previa?: PreviaAtualizacao | null): string | null {
  if (!previa || !previa.proposta_gerada_id || previa.nao_encontrada) return null;
  const num = String(numero || previa.numero_proposta || '?').trim().toUpperCase();
  const temRevisao = previa.revisao !== null && previa.revisao !== undefined && String(previa.revisao) !== '';
  const partes = [`${num}${temRevisao ? ` R${previa.revisao}` : ''}`];
  if (previa.titulo_documento) partes.push(cortarTexto(previa.titulo_documento, 90));
  partes.push(previa.negocio_id !== null && previa.negocio_id !== undefined
    ? `negócio nº ${previa.negocio_id}${previa.negocio_titulo ? ` — ${cortarTexto(previa.negocio_titulo, 70)}` : ''}`
    : 'sem negócio');
  return partes.join(' · ');
}

// Rótulo do card bloqueado (status 'invalida'), por motivo.
export function rotuloBloqueio(motivoCodigo?: string | null): string {
  if (motivoCodigo === 'proposta_mudou') return '⚠ bloqueado: a proposta mudou';
  if (motivoCodigo === 'previa_indisponivel') return '⚠ bloqueado: não consegui conferir a proposta';
  if (motivoCodigo === 'numero_sem_origem') return '⛔ bloqueado: não deu para confirmar qual proposta';
  if (motivoCodigo === 'negocio_divergente') return '⛔ bloqueado: proposta de outro negócio';
  if (motivoCodigo === 'sem_precondicao') return '⛔ bloqueado: card antigo, sem conferência da proposta';
  if (motivoCodigo === 'card_anterior_a_leitura') return '⛔ bloqueado: card de antes da correção de 22/09 — peça de novo';
  return '⚠ incompleta';
}

export interface ResumoAtualizacao {
  texto: string;       // uma linha para o card
  linhas: string[];    // "Proposta X", um campo por linha e o que muda em cada um
  n_campos: number;    // quantos CAMPOS (as linhas de mudança não contam — 22/09/2026)
  alerta: boolean;     // vermelho: encolhe demais ou proposta já enviada/aprovada
  avisos: string[];    // frases para a pessoa, uma por motivo
}

export interface GrupoPreviaComposicao {
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  total_venda: number;
}

export interface ItemPreviaComposicao {
  descricao: string;
  unidade: string;
  quantidade: number;
  coeficiente: number;
  valor_unitario: number;
  markup_pct: number;
  origem: 'catalogo' | 'livre';
}

export interface PreviaCriacaoComposicao {
  tipo: 'criacao_composicao';
  status_destino: string;
  total_custo: number;
  total_venda: number;
  markup_padrao_pct: number;
  grupos: GrupoPreviaComposicao[];
  itens: ItemPreviaComposicao[];
}

// Encolhimento acima disto é suspeito: reescrita destrutiva, não ajuste.
export const LIMIAR_ENCOLHIMENTO = 0.30;

export function formatarMilhar(n: number): string {
  return String(Math.max(0, Math.round(n))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

// Ausência não vira zero: Number(null), Number('') e Number(false) dão 0, e um
// custo que não veio apareceria como "R$ 0,00" no card de aprovação.
function numeroFinito(valor: unknown): number | null {
  if (valor === null || valor === undefined || typeof valor === 'boolean') return null;
  if (typeof valor === 'string' && !valor.trim()) return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

// Formatação deliberadamente pura e estável: os testes não dependem do locale
// instalado no navegador/Node e nenhum valor financeiro é recalculado aqui.
export function formatarDinheiroComposicao(valor: unknown): string {
  const n = numeroFinito(valor);
  if (n === null) return '—';
  const sinal = n < 0 ? '-' : '';
  const [inteiro, centavos] = Math.abs(n).toFixed(2).split('.');
  return `${sinal}R$ ${inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${centavos}`;
}

export function formatarNumeroComposicao(valor: unknown, casas = 4): string {
  const n = numeroFinito(valor);
  if (n === null) return '—';
  const sinal = n < 0 ? '-' : '';
  const fixo = Math.abs(n).toFixed(casas);
  const [inteiro, decimais = ''] = fixo.split('.');
  const parteDecimal = decimais.replace(/0+$/, '');
  const parteInteira = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${sinal}${parteInteira}${parteDecimal ? `,${parteDecimal}` : ''}`;
}

// A prévia vem do JSON gravado na mensagem: um elemento nulo derrubaria o
// drawer inteiro no `grupo.descricao`. Sem forma completa, o card cai no
// resumo antigo em vez de quebrar.
export function ehPreviaCriacaoComposicao(previa: any): previa is PreviaCriacaoComposicao {
  const ehObjeto = (x: unknown) => typeof x === 'object' && x !== null && !Array.isArray(x);
  return previa?.tipo === 'criacao_composicao'
    && Array.isArray(previa.grupos) && previa.grupos.every(ehObjeto)
    && Array.isArray(previa.itens) && previa.itens.every(ehObjeto);
}

export function resumirCriacaoComposicao(input: any, previa: PreviaCriacaoComposicao): string {
  const nome = String(input?.nome || 'Composição');
  const status = previa.status_destino || '—';
  const markup = formatarNumeroComposicao(previa.markup_padrao_pct, 2);
  return `${nome} · será criada ${status} · custo ${formatarDinheiroComposicao(previa.total_custo)}`
    + ` · venda ${formatarDinheiroComposicao(previa.total_venda)} · markup ${markup}%`;
}

export function rotuloCampo(campo: string): string {
  return ROTULO_CAMPO_PROPOSTA[campo] || campo;
}

// Campos de conteúdo presentes no input (mesma lista do backend, montarDadosEstruturados).
export function camposDoInput(input: any): string[] {
  if (!input || typeof input !== 'object') return [];
  return CAMPOS_TEXTO_PROPOSTA.filter(c => input[c] != null && String(input[c]).trim() !== '');
}

// `alarmar: false` no card já aplicado/descartado: o que muda continua escrito,
// mas sem o "⚠ encolhe" — o alarme é para decidir, não para o histórico.
export function resumirAtualizacaoProposta(input: any, previa?: PreviaAtualizacao | null, opcoes: { alarmar?: boolean } = {}): ResumoAtualizacao {
  const alarmar = opcoes.alarmar !== false;
  const numero = (input && input.numero_proposta) || (previa && previa.numero_proposta) || '?';
  const avisos: string[] = [];
  let alerta = false;

  if (alarmar && previa && previa.nao_encontrada) {
    avisos.push(`proposta ${numero} não encontrada — a ação vai falhar`);
    alerta = true;
  }
  const status = previa && previa.status_doc ? String(previa.status_doc) : '';
  if (alarmar && (status === 'enviada' || status === 'aprovada')) {
    avisos.push(`esta proposta já foi ${status === 'enviada' ? 'ENVIADA ao cliente' : 'APROVADA'} — reescrever muda o que ele recebeu`);
    alerta = true;
  }

  const partes: string[] = [];
  // Linhas do card: a do campo e, logo abaixo, o que muda nele.
  const detalhadas: string[] = [];
  const porCampo = new Map<string, PreviaCampo>();
  for (const c of (previa && previa.campos) || []) porCampo.set(c.campo, c);
  const campos = camposDoInput(input);
  // Array.from, não spread: o tsconfig do CRA mira ES5 e recusa iterar Map.keys() com `...`.
  const lista = campos.length ? campos : Array.from(porCampo.keys());
  for (const campo of lista) {
    const p = porCampo.get(campo);
    if (!p) { partes.push(rotuloCampo(campo)); detalhadas.push(rotuloCampo(campo)); continue; }
    let s = `${rotuloCampo(campo)} (${formatarMilhar(p.chars_antes)} → ${formatarMilhar(p.chars_depois)} caracteres)`;
    const m = p.mudancas;
    if (m && typeof m.intactos === 'number' && m.total > 0) s += ` · ${m.intactos} de ${m.total} parágrafos iguais`;
    if (alarmar && p.chars_antes > 0 && p.chars_depois < p.chars_antes * (1 - LIMIAR_ENCOLHIMENTO)) {
      const pct = Math.round((1 - p.chars_depois / p.chars_antes) * 100);
      s += ` ⚠ encolhe ${pct}%`;
      avisos.push(`${rotuloCampo(campo)} encolhe ${pct}% — confira se não está perdendo texto escrito à mão`
        + (lista.length > 1 ? '. O card aplica todos os campos juntos: para manter este campo como está, descarte e peça de novo sem ele' : ''));
      alerta = true;
    }
    partes.push(s);
    detalhadas.push(s);
    detalhadas.push(...linhasDasMudancas(m));
    // Frase de HOJE que não tem nem uma parecida no texto novo: pode ser o que a
    // pessoa escreveu à mão. É o vermelho — decidir, não descobrir depois.
    const somem = (m && m.somem) || [];
    if (alarmar && somem.length) {
      const n = (m && typeof m.n_somem === 'number') ? m.n_somem : somem.length;
      avisos.push(`${n === 1 ? 'uma frase de hoje some' : `${n} frases de hoje somem`} da ${rotuloCampo(campo)}`
        + ` (ex.: «${somem[0]}») — confira se não é algo que você escreveu`);
      alerta = true;
    }
  }
  const alvo = descreverAlvoProposta(numero, previa) || numero;
  const texto = `Proposta ${alvo}${partes.length ? ` · ${partes.join(' · ')}` : ' · (nenhum campo de conteúdo)'}`;
  const linhas = [`Proposta ${alvo}`, ...(detalhadas.length ? detalhadas : ['(nenhum campo de conteúdo)'])];
  return { texto, linhas, n_campos: partes.length, alerta, avisos };
}

// As linhas "troca / entra / sai" de um campo, no máximo 3 de cada e o resto contado.
export function linhasDasMudancas(m?: MudancasCampo | null): string[] {
  if (!m) return [];
  const saida: string[] = [];
  const mais = (mostrados: number, total?: number | null) => (typeof total === 'number' && total > mostrados ? ` (e mais ${total - mostrados})` : '');
  const trocas = (m.trocas || []).slice(0, 3);
  trocas.forEach((t, i) => saida.push(`↺ troca «${t.antes}» por «${t.depois}»${i === trocas.length - 1 ? mais(trocas.length, m.n_trocas) : ''}`));
  const novos = (m.novos || []).slice(0, 3);
  novos.forEach((t, i) => saida.push(`+ entra «${t}»${i === novos.length - 1 ? mais(novos.length, m.n_novos) : ''}`));
  const somem = (m.somem || []).slice(0, 3);
  somem.forEach((t, i) => saida.push(`− sai «${t}»${i === somem.length - 1 ? mais(somem.length, m.n_somem) : ''}`));
  return saida;
}

// "1m20s" para o contador do drawer — 5 minutos em segundos corridos parecem travados.
export function formatarDuracao(segundos: number): string {
  const s = Math.max(0, Math.floor(segundos));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m${r < 10 ? '0' : ''}${r}s`;
}

// Texto que o botão "Pedir de novo" põe no composer. "Um campo por vez" só faz
// sentido para quem tem campos separáveis (edição/geração de proposta); criar
// composição com 30 itens ou criar negócio pedem "mais curto", não "um campo".
export function textoPedirDeNovo(
  tool: string, rotulo: string, numeroProposta?: string | null,
  ctx: { status?: string; candidatos?: string[] | null; motivo_codigo?: string | null } = {},
): string {
  // Card bloqueado porque a proposta mudou no meio da rodada: tamanho não foi o
  // problema — "em partes menores" mandaria reescrever sobre a versão velha.
  if (ctx.motivo_codigo === 'proposta_mudou') {
    return `Confira a versão atual${numeroProposta ? ` da ${numeroProposta}` : ''} e refaça as alterações (ação "${rotulo}").`;
  }
  if (ctx.motivo_codigo === 'previa_indisponivel') {
    return `Refaça a ação "${rotulo}"${numeroProposta ? `, na ${numeroProposta}` : ''}.`;
  }
  // Bloqueado por proposta errada (15/09/2026): o problema é QUAL proposta. O texto
  // NÃO cita o número bloqueado nem afirma qual é o certo: enviado como está, ele
  // viraria "número escrito pela pessoa" — justo o que a trava rejeitou — e no
  // bloqueio falso a proposta certa É a do card (revisão adversarial de 15/09).
  if (ctx.motivo_codigo === 'numero_sem_origem' || ctx.motivo_codigo === 'negocio_divergente') {
    return `Confira a proposta: se é a mesma do card, responda confirmando o número; se não, diga qual (PRP-AAAA-NNNN). Refaça a ação "${rotulo}".`;
  }
  if (ctx.motivo_codigo === 'sem_precondicao') {
    return `Refaça a ação "${rotulo}"${numeroProposta ? ` na ${numeroProposta}` : ''}: o card antigo não pode ser aplicado.`;
  }
  // Card de antes da trava de leitura (22/09/2026): refazer é ler a versão atual
  // e partir dela — nunca repetir o texto do card antigo.
  if (ctx.motivo_codigo === 'card_anterior_a_leitura') {
    return `Leia a versão atual${numeroProposta ? ` da ${numeroProposta}` : ''} e refaça a ação "${rotulo}" partindo dela, mantendo o que eu mudei à mão.`;
  }
  // Proposta NOVA não se pede "em partes": não existe segundo turno que junte
  // as partes, e foi assim que o documento virou um resumo do briefing (13/09/2026).
  if (tool === 'gerar_proposta') {
    return `Refaça a proposta completa em um único documento, com todas as seções desenvolvidas e formatadas (ação "${rotulo}").`;
  }
  const porCampo = tool === 'atualizar_proposta_documento' || tool === 'editar_trecho_proposta';
  const onde = numeroProposta ? `, na ${numeroProposta}` : '';
  // Incompleta por falta de proposta: o problema não é tamanho, é QUAL — a
  // orientação "um campo por vez" mandaria a pessoa ao mesmo erro.
  if (ctx.status === 'invalida' && !numeroProposta) {
    const cands = (ctx.candidatos || []).filter(Boolean);
    if (cands.length >= 2) return `Atualize a ${cands[0]} (e não a ${cands.slice(1).join(', ')}): refaça a ação "${rotulo}".`;
    return `Diga qual proposta (número PRP-…) e refaça a ação "${rotulo}".`;
  }
  return porCampo
    ? `Refaça a ação "${rotulo}" em partes menores — um campo por vez${onde}.`
    : `Refaça a ação "${rotulo}" mais curta (menos itens, textos menores)${onde}.`;
}

// Linha do trace quando a chamada ao modelo parou pelo limite ou pelo relógio; null quando não parou.
export function descreverParada(p: any): string | null {
  if (!p) return null;
  if (p.stop_reason === 'max_tokens') return `⚠ parou no limite de tamanho (${p.tokens_out ?? '?'}/${p.max_tokens_config ?? '?'})`;
  if (p.stop_reason === 'malformed_tool_use' || p.stop_reason === 'malformed_model_output') return `⚠ resposta malformada (${p.stop_reason})`;
  if (p.stop_reason === 'abortado_por_tempo') return '⚠ abortada pelo relógio da Lambda';
  return null;
}

// Passos do ciclo que não são chamada nem tool: o que o robô decidiu sozinho e por quê.
export function descreverPassoDeControle(p: any): string | null {
  if (!p) return null;
  if (p.etapa === 'auto_recuperar_truncamento') return `↻ Resposta cortada — pediu de novo em partes menores${p.motivo ? '' : ''}`;
  if (p.etapa === 'truncamento_recuperado') {
    const n = Array.isArray(p.acoes_substitutas) ? p.acoes_substitutas.length : 0;
    return `✓ Resposta cortada refeita automaticamente${n ? ` em ${n} parte${n === 1 ? '' : 's'}` : ''}`;
  }
  if (p.etapa === 'edicoes_consolidadas') {
    if (p.consolidado === false) return `⚠ Edições da ${p.numero_proposta || 'proposta'} não foram reunidas (${p.motivo || 'conflito'})`;
    const n = typeof p.n_campos === 'number' ? p.n_campos : 0;
    return p.divergente
      ? `⚠ ${p.numero_proposta || 'Proposta'} mudou durante a rodada — o card saiu bloqueado, nada será gravado`
      : `⧉ ${n} campo${n === 1 ? '' : 's'} da ${p.numero_proposta || 'proposta'} reunido${n === 1 ? '' : 's'} em 1 card`;
  }
  if (p.etapa === 'alvo_proposta_bloqueado') {
    const porque = p.motivo_codigo === 'negocio_divergente' ? 'é de outro negócio' : 'não teve a origem confirmada na conversa';
    return `⛔ Card da ${p.numero_proposta || 'proposta'} bloqueado: a proposta ${porque} — nada será gravado`;
  }
  // Ação recusada não vira mais cartão (16/09/2026): o motivo vai no texto do
  // Dwight, e aqui fica o rastro para quem abre "como ele chegou nisso".
  if (p.etapa === 'acao_recusada') {
    const motivo = p.motivo_codigo === 'texto_fora_da_regua'
      ? 'texto fora da régua (valor, dado bancário ou campo longo demais)'
      : (p.motivo || 'faltou informação');
    return `⛔ ${p.rotulo || p.tool || 'Ação'} não foi preparada: ${motivo}`;
  }
  // Ler antes de escrever (22/09/2026, PRP-2026-0178).
  if (p.etapa === 'leitura_documento') {
    return `📖 Leu a ${p.numero_proposta || 'proposta'} como ela está agora${p.lida_por === 'servidor' ? ' (o sistema entregou ao Dwight)' : ''}`;
  }
  if (p.etapa === 'escrita_recusada_leitura') {
    const onde = `${p.campo ? rotuloCampo(p.campo) : 'o texto'} da ${p.numero_proposta || 'proposta'}`;
    if (p.motivo_codigo === 'leitura_desatualizada') return `⛔ ${onde} mudou depois da leitura — nada foi gravado`;
    if (p.motivo_codigo === 'perderia_imagem') return `⛔ Reescrever ${onde} apagaria uma imagem — recusado`;
    if (p.motivo_codigo === 'leitura_incompleta') return `⛔ ${onde} é grande demais para reescrever inteiro — recusado`;
    return `⛔ Tentou alterar ${onde} sem ler a versão atual — nada foi gravado`;
  }
  if (p.etapa === 'auto_recuperar_leitura') return '↻ Entregou ao Dwight a versão atual da proposta para ele refazer a partir dela';
  if (p.etapa === 'recuperacao_truncamento_incompleta') return '⚠ A nova tentativa não refez a ação cortada';
  if (p.etapa === 'auto_recuperar_invalida') return '↻ Ação sem campo obrigatório — devolveu o erro ao modelo';
  if (p.etapa === 'auto_continuar_lote_proposta') return `↻ Lote de ${p.n_acoes ?? '?'} campos registrado — pediu o próximo`;
  if (p.etapa === 'orcamento_tempo') return `⏱ Parou sem chamar o modelo: ${p.motivo || 'sem tempo'}${typeof p.sobra_ms === 'number' ? ` (sobravam ${Math.round(p.sobra_ms / 1000)} s)` : ''}`;
  if (p.etapa === 'auto_continuar') return `↻ Turn extra: ${p.motivo || 'anunciou ação sem chamar tool'}`;
  return null;
}

// Rótulo dos campos atualizados que o backend devolve em resultado.atualizada.
export function resumirCamposAtualizados(campos: string[] | undefined | null): string {
  const lista = Array.isArray(campos) ? campos : [];
  if (!lista.length) return 'Aplicado';
  return `Campos atualizados: ${lista.map(rotuloCampo).join(', ')}`;
}

// Reserva do "Aplicar" (14/09/2026): o servidor marca o card 'aplicando' ANTES de
// gravar, e o segundo pedido do mesmo card recebe 409. Reserva recente = alguém
// está gravando agora (outro toque, o "sim" do Telegram, outra aba). Reserva mais
// velha que APLICANDO_EXPIRA_MS — a mesma régua do agentes-engine — é gravação que
// começou e não confirmou (a Lambda caiu no meio): pode ter gravado ou não.
export const APLICANDO_EXPIRA_MS = 10 * 60 * 1000;

export function situacaoAplicando(
  acao: { status?: string; aplicando_desde?: string | null } | null | undefined, agora = Date.now(),
): 'em_andamento' | 'sem_confirmacao' | null {
  if (acao?.status !== 'aplicando') return null;
  const desde = Date.parse(acao.aplicando_desde || '');
  return Number.isFinite(desde) && agora - desde <= APLICANDO_EXPIRA_MS ? 'em_andamento' : 'sem_confirmacao';
}

export function textoAplicacaoSemConfirmacao(aplicandoDesde?: string | null): string {
  const desde = Date.parse(aplicandoDesde || '');
  const quando = Number.isFinite(desde)
    ? ` em ${new Date(desde).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
    : '';
  return `A gravação começou${quando} e não confirmou — pode ter gravado ou não. Confira no sistema antes de tentar de novo.`;
}

// Card aplicado de criar_negocio. O responsável só é mencionado quando o
// resultado é do formato novo (14/09/2026: a chave `responsavel` existe mesmo
// nula). Card antigo não tem a chave e diria "sem responsável" em negócio já
// acertado à mão. "criado sem responsável" é foto da criação — continua
// verdadeiro depois que alguém define o responsável na aba Editar.
export function resumirNegocioCriado(criado: any): string {
  const base = `Negócio criado: ${criado?.titulo ?? ''}`;
  if (!criado || typeof criado !== 'object' || !('responsavel' in criado)) return base;
  return criado.responsavel ? `${base} · responsável: ${criado.responsavel}` : `${base} · criado sem responsável`;
}
